import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import WarningIcon from '@material-ui/icons/Warning';
import BuildIcon from '@material-ui/icons/Build';
import { AssetStatistics, AssetCategory } from '../../types';
import { AssetRegisterService } from '../../services/assetRegisterService';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(3),
  },
  card: {
    height: '100%',
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  statValue: {
    fontSize: '2.5rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  icon: {
    fontSize: '3rem',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  section: {
    marginTop: theme.spacing(3),
  },
  categoryRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

export const DashboardView: React.FC = () => {
  const classes = useStyles();
  const [stats, setStats] = useState<AssetStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const service = React.useMemo(
    () => new AssetRegisterService(discoveryApi, fetchApi),
    [discoveryApi, fetchApi],
  );

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const statistics = await service.getStatistics();
        setStats(statistics);
        setError(null);
      } catch (err) {
        setError('Failed to load asset statistics. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!stats) {
    return <Typography>No statistics available.</Typography>;
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const categoryLabels: Record<AssetCategory, string> = {
    [AssetCategory.HARDWARE]: 'Hardware',
    [AssetCategory.SOFTWARE]: 'Software',
    [AssetCategory.NETWORK]: 'Network Equipment',
    [AssetCategory.INFRASTRUCTURE]: 'Infrastructure',
    [AssetCategory.MOBILE_DEVICE]: 'Mobile Devices',
    [AssetCategory.PERIPHERALS]: 'Peripherals',
    [AssetCategory.LICENSE]: 'Licenses',
  };

  return (
    <Box className={classes.container}>
      <Typography variant="h4" gutterBottom>
        Asset Register Dashboard
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card className={classes.card}>
            <CardContent className={classes.statCard}>
              <TrendingUpIcon className={classes.icon} />
              <Typography className={classes.statValue}>
                {stats.totalAssets}
              </Typography>
              <Typography className={classes.statLabel} variant="body2">
                Total Assets
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card className={classes.card}>
            <CardContent className={classes.statCard}>
              <TrendingUpIcon className={classes.icon} />
              <Typography className={classes.statValue}>
                {formatCurrency(stats.totalValue)}
              </Typography>
              <Typography className={classes.statLabel} variant="body2">
                Total Asset Value
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card className={classes.card}>
            <CardContent className={classes.statCard}>
              <WarningIcon
                className={`${classes.icon} ${classes.warningIcon}`}
              />
              <Typography className={classes.statValue}>
                {stats.expiringWarranties.length +
                  stats.expiringLicenses.length}
              </Typography>
              <Typography className={classes.statLabel} variant="body2">
                Expiring Soon
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Assets by Category
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(stats.byCategory)
                .filter(([_, count]) => count > 0)
                .map(([category, count]) => (
                  <TableRow key={category} className={classes.categoryRow}>
                    <TableCell>
                      <Chip
                        label={categoryLabels[category as AssetCategory]}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{count}</TableCell>
                    <TableCell align="right">
                      {((count / stats.totalAssets) * 100).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {stats.expiringWarranties.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            <WarningIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Expiring Warranties (Next 30 Days)
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Warranty Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.expiringWarranties.map(asset => (
                  <TableRow key={asset.id}>
                    <TableCell>{asset.assetTag}</TableCell>
                    <TableCell>{asset.name}</TableCell>
                    <TableCell>{asset.location}</TableCell>
                    <TableCell>
                      {asset.warrantyExpiry
                        ? new Date(asset.warrantyExpiry).toLocaleDateString()
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {stats.expiringLicenses.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            <WarningIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Expiring Licenses (Next 30 Days)
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>License Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.expiringLicenses.map(asset => (
                  <TableRow key={asset.id}>
                    <TableCell>{asset.assetTag}</TableCell>
                    <TableCell>{asset.name}</TableCell>
                    <TableCell>
                      {asset.licenseExpiry
                        ? new Date(asset.licenseExpiry).toLocaleDateString()
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {stats.maintenanceDue.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            <BuildIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Maintenance Due
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Last Maintenance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.maintenanceDue.map(asset => (
                  <TableRow key={asset.id}>
                    <TableCell>{asset.assetTag}</TableCell>
                    <TableCell>{asset.name}</TableCell>
                    <TableCell>{asset.location}</TableCell>
                    <TableCell>
                      {asset.lastMaintenanceDate
                        ? new Date(
                            asset.lastMaintenanceDate,
                          ).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};
